// ¿ESTO ES DE AHORA, O LA PC ESTÁ APAGADA?
//
// Va antes que todo lo demás porque lo habilita: si la PC del estacionamiento
// dejó de enviar hace seis horas, cada número de esta pantalla es una foto vieja
// presentada como si fuera el presente. Y hoy no hay forma de saberlo — el
// dashboard muestra lo último que llegó, sin decir cuándo llegó.
//
// El umbral no puede ser fijo. "Hace seis horas" a las tres de la mañana es
// normal y no debe alarmar a nadie; a las once de la mañana significa que algo
// se rompió. Por eso el estado se cruza con la curva de ocupación: si a esta
// hora el estacionamiento suele estar vacío, el silencio es esperable.

import React, { useMemo } from "react";
import { UI, POLARIDAD, ESTADO } from "./theme";
import { fecha } from "./format";

const MIN = 60 * 1000;

/** Parsea el `ts` del documento live: hora LOCAL de la PC, sin zona. */
function parseTs(ts) {
  if (!ts) return null;
  const d = new Date(String(ts).length <= 19 ? `${ts}` : ts);
  return isNaN(d.getTime()) ? null : d;
}

function texto(min) {
  if (min < 2) return "recién";
  if (min < 60) return `hace ${Math.round(min)} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} hora${h === 1 ? "" : "s"}`;
  const d = Math.round(h / 24);
  return `hace ${d} día${d === 1 ? "" : "s"}`;
}

/** El día del último documento diario que llegó, o null. */
function ultimoDiario(cerrados) {
  for (let i = cerrados.length - 1; i >= 0; i--) {
    if (cerrados[i]?.date) return cerrados[i].date;
  }
  return null;
}

const EstadoDelDato = ({ vivo, cerrados }) => {
  const estado = useMemo(() => {
    // ── El historial puede morir con el pulso vivo ─────────────────────
    //
    // La frescura miraba SOLO el documento `live`, que la PC pisa cada pocos
    // minutos. Pero los documentos DIARIOS viajan por otro camino y pueden
    // dejar de llegar solos.
    //
    // Caso real, cliente 3f4629213f: `live.ts` de hoy 20:07 —o sea la PC
    // enviando ahora mismo— y el último diario del 26 de julio. La pantalla
    // decía "En vivo · actualizado recién" mientras el historial estaba
    // congelado hacía cinco semanas, y todos los bloques mostraban julio
    // creyendo que era el último mes.
    //
    // Se avisa aunque el pulso esté perfecto: es el caso MÁS peligroso,
    // porque nada más en la pantalla lo delata.
    const ultimo = ultimoDiario(cerrados || []);
    if (ultimo) {
      const dias = Math.floor(
        (Date.now() - new Date(`${ultimo}T00:00:00`).getTime()) / 86400000
      );
      if (dias >= 3) return { nivel: "historial_viejo", ultimo, dias };
    }

    const ts = parseTs(vivo?.ts);
    if (!ts) return { nivel: "sin_dato" };

    const min = (Date.now() - ts.getTime()) / MIN;

    // ¿A esta hora suele haber movimiento? Si el estacionamiento normalmente
    // está cerrado ahora, el silencio no es una falla.
    const h = new Date().getHours();
    const curvas = cerrados
      .map((d) => d?.occupancy?.hourly)
      .filter((c) => Array.isArray(c) && c.length === 24)
      .slice(-14);
    let horaOperativa = true;   // sin curva, se asume que sí: mejor avisar de más
    if (curvas.length >= 3) {
      const promedioEnEstaHora =
        curvas.reduce((s, c) => s + (Number(c[h]) || 0), 0) / curvas.length;
      horaOperativa = promedioEnEstaHora >= 1;
    }

    if (min < 10) return { nivel: "fresco", min, ts };
    if (min < 120 || !horaOperativa) return { nivel: "dudoso", min, ts, horaOperativa };
    return { nivel: "frio", min, ts };
  }, [vivo, cerrados]);

  if (estado.nivel === "sin_dato") return null;

  // Frío en horario de operación: no es una nota al pie, es lo primero que hay
  // que leer. Ocupa el ancho completo y empuja al resto hacia abajo, porque
  // todo lo de abajo quedó desactualizado en el mismo momento.
  // Historial congelado con el pulso vivo. Va en rojo y ancho completo: todo lo
  // que hay debajo son números viejos presentados como actuales, y a diferencia
  // del caso "frío" acá NADA más en la pantalla lo delata.
  if (estado.nivel === "historial_viejo") {
    return (
      <div style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        background: "#fdecec", border: `1px solid ${ESTADO.alerta.color}`,
        borderRadius: 8, padding: "12px 14px", marginBottom: 16,
      }}>
        <span className="material-symbols-rounded" style={{ fontSize: 22, color: ESTADO.alerta.color }}>
          sync_problem
        </span>
        <div>
          <div style={{ fontWeight: 700, color: UI.texto, fontSize: 14 }}>
            No llegan datos nuevos desde el {fecha(estado.ultimo)}, hace {estado.dias} días.
          </div>
          <div style={{ fontSize: 13, color: UI.textoSuave, marginTop: 2 }}>
            El estacionamiento sigue conectado, así que lo de hoy se ve bien, pero los
            resúmenes de cada día dejaron de llegar. Todo lo que ves más abajo es de
            antes de esa fecha. Avísale a soporte.
          </div>
        </div>
      </div>
    );
  }

  if (estado.nivel === "frio") {
    const cuando = estado.ts.toLocaleString("es-AR", {
      weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
    });
    return (
      <div style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        background: "#fdecec", border: `1px solid ${ESTADO.alerta.color}`,
        borderRadius: 8, padding: "12px 14px", marginBottom: 16,
      }}>
        <span className="material-symbols-rounded" style={{ fontSize: 22, color: ESTADO.alerta.color }}>
          cloud_off
        </span>
        <div>
          <div style={{ fontWeight: 700, color: UI.texto, fontSize: 14 }}>
            La PC del estacionamiento no envía datos desde {cuando}.
          </div>
          <div style={{ fontSize: 13, color: UI.textoSuave, marginTop: 2 }}>
            Todo lo que ves abajo es de ese momento. Revisa que TicketControl esté
            abierto y con conexión.
          </div>
        </div>
      </div>
    );
  }

  const fresco = estado.nivel === "fresco";
  const color = fresco ? POLARIDAD.entra.color : ESTADO.anulado.color;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 7,
      fontSize: 12.5, color: UI.textoSuave, marginBottom: 12,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: "50%", background: color,
        display: "inline-block", flexShrink: 0,
      }} />
      {fresco
        ? <>Datos en vivo · actualizado {texto(estado.min)}</>
        : <>Último dato {texto(estado.min)}{estado.horaOperativa === false ? " · a esta hora suele estar cerrado" : ""}</>}
    </div>
  );
};

export default EstadoDelDato;
